import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "../components/Header";
import { Catalogs } from "../components/Catalogs";
import { ContactFooter } from "../components/ContactFooter";
import { BrandLogo } from "../components/FormExtras";
import { BRAND_GROUPS, slug } from "../formData";
import LOGOS from "../brandLogos.json";
import { useLanguage } from "../i18n";

// Logo wall in the cover: the brands that have a full logo, each once, in catalogue order
const WALL = [...new Map(BRAND_GROUPS.flatMap((g) => g.brands).filter((b) => LOGOS[slug(b.name)] === "logo").map((b) => [slug(b.name), b])).values()].slice(0, 9);

export default function BrandsPage() {
  const { t } = useLanguage();
  const c = t.catalogs;
  return (
    <div data-testid="brands-page" className="min-h-screen bg-cream">
      <Header />
      <main>
        {/* Cover: the page's own title and intro on the left, a wall of brand logos on the right */}
        <section data-testid="brands-heading" className="mt-14 pt-14 lg:pt-16 pb-14 lg:pb-20 bg-sand overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inksoft">
                <Link to="/" className="hover:text-ink">Home</Link> · {t.nav.brands}
              </p>
              <h1 data-testid="brands-title" className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink">{c.title}</h1>
              <span aria-hidden="true" className="block mt-8 h-px w-24 bg-[#C5A059]" />
              <p className="mt-8 max-w-lg text-base lg:text-lg leading-relaxed text-inksoft">{c.subtitle}</p>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-amberdark">{c.overline}</p>
            </motion.div>
            <motion.div data-testid="brands-wall" className="lg:col-span-6 grid grid-cols-3 gap-px bg-ink/10 border border-ink/10"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
              {WALL.map((b) => (
                <a key={slug(b.name)} href={b.url || undefined} target="_blank" rel="noopener noreferrer" data-testid={`brands-wall-${slug(b.name)}`}
                  className="group bg-cream flex items-center justify-center aspect-[4/3] px-4 sm:px-6 transition-colors hover:bg-white">
                  <BrandLogo name={b.name} className="bw-hover mx-auto transition-transform duration-500 group-hover:scale-105" />
                </a>
              ))}
            </motion.div>
          </div>
        </section>
        {/* Quick links to each family of brands */}
        <nav data-testid="brands-groups-nav" className="sticky top-14 z-30 bg-cream/95 backdrop-blur border-y border-ink/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 flex gap-2 overflow-x-auto py-3">
            {BRAND_GROUPS.map((g, i) => (
              <a key={g.key} href={`#brand-group-${g.key}`} data-testid={`brands-nav-${g.key}`}
                className="shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-ink/15 font-mono text-[10.5px] uppercase tracking-[0.12em] text-inksoft hover:border-[#C5A059] hover:text-ink transition-colors">
                <span className="text-[#C5A059]">{String(i + 1).padStart(2, "0")}</span>{g.label.split(" — ")[0]}
              </a>
            ))}
          </div>
        </nav>
        <Catalogs bare />
      </main>
      <ContactFooter />
    </div>
  );
}
